import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPopularThreads, getRecentThreads } from '../../services/boardService';
import { CATEGORIES, HOME_CATEGORIES, getCategoryById } from '../../data/boardCategories';

function SectionHeader({ title }) {
  return (
    <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '0' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ui-accent-text)', margin: 0 }}>
        {title}
      </h2>
    </div>
  );
}

function ThreadRow({ thread }) {
  const category = getCategoryById(thread.categoryId);
  return (
    <Link
      to={`/board/${thread.categoryId}/${thread.subCategoryId}/${thread.id}`}
      className="surface-link revisit-card"
      style={{ display: 'flex', alignItems: 'center', gap: '16px', borderRadius: '12px', padding: '10px 16px' }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ui-accent-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {thread.title}
        </p>
        <p className="revisit-meta" style={{ marginTop: '4px', fontSize: '12px' }}>
          {category?.name ?? thread.categoryId}
        </p>
      </div>
      <div className="revisit-meta" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px' }}>
        <span>返信 {thread.replyCount}</span>
        <span>いいね {thread.likesCount}</span>
      </div>
    </Link>
  );
}

function BoardHome() {
  const [popularThreads, setPopularThreads] = useState([]);
  const [recentThreads, setRecentThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getPopularThreads(5), getRecentThreads(5)])
      .then(([popular, recent]) => {
        setPopularThreads(popular);
        setRecentThreads(recent);
      })
      .catch((err) => console.error('[BoardHome]', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ maxWidth: '896px', margin: '0 auto', padding: '32px 16px' }}>
        <div className="revisit-panel" style={{ borderRadius: '16px', padding: '20px' }}>
          <p className="revisit-meta" style={{ fontSize: '14px' }}>読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '896px', margin: '0 auto', padding: '32px 16px', display: 'flex', flexDirection: 'column', gap: '40px' }}>
      {/* ヘッダー */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--ui-accent-text)', margin: 0 }}>
            📋 掲示板
          </h1>
          <p className="revisit-meta" style={{ fontSize: '13px', marginTop: '4px' }}>
            登山の悩み・装備・山行報告をみんなで共有しましょう
          </p>
        </div>
        <Link
          to="/board/new-thread"
          style={{
            backgroundColor: 'var(--ch-thread-btn)',
            color: '#ffffff',
            padding: '8px 20px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          スレッドを作成
        </Link>
      </div>

      {/* カテゴリ一覧 */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SectionHeader title="カテゴリ一覧" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
          {HOME_CATEGORIES.map((cat) => (
            <Link
              key={cat.id}
              to={cat.path}
              className="surface-link revisit-card"
              style={{ display: 'block', borderRadius: '16px', padding: '16px 20px' }}
            >
              <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--ui-accent-text)', margin: 0 }}>
                {cat.title}
              </p>
              <p className="revisit-meta" style={{ fontSize: '14px', marginTop: '8px' }}>
                {cat.description}
              </p>
            </Link>
          ))}
        </div>

        {/* 全カテゴリ一覧 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.id}
              to={`/board/${cat.id}`}
              className="surface-link"
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 16px',
                borderRadius: '10px',
                border: '1px solid var(--ch-border)',
                backgroundColor: 'var(--ch-bg-post)',
                color: 'var(--ch-text)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {cat.name}
              <span className="revisit-meta" style={{ fontSize: '12px', marginLeft: '8px' }}>
                （{cat.subCategories.length}カテゴリ）
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 盛り上がり中 */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SectionHeader title="盛り上がり中のスレッド" />
        {popularThreads.length === 0 ? (
          <div className="revisit-panel" style={{ borderRadius: '16px', padding: '20px' }}>
            <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ui-accent-text)', margin: 0 }}>
              これから盛り上がる話題を待っています
            </p>
            <p className="revisit-meta" style={{ fontSize: '14px', marginTop: '4px' }}>
              相談や体験談が集まると、ここに会話の熱が見えてきます。
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {popularThreads.map((t) => <ThreadRow key={t.id} thread={t} />)}
          </div>
        )}
      </section>

      {/* 新着スレッド */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SectionHeader title="新着スレッド" />
        {recentThreads.length === 0 ? (
          <div className="revisit-panel" style={{ borderRadius: '16px', padding: '20px' }}>
            <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ui-accent-text)', margin: 0 }}>
              新着スレッドはまだありません
            </p>
            <p className="revisit-meta" style={{ fontSize: '14px', marginTop: '4px' }}>
              小さな相談ひとつから、新しい登山のきっかけが生まれます。
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {recentThreads.map((t) => <ThreadRow key={t.id} thread={t} />)}
          </div>
        )}
      </section>
    </div>
  );
}

export default BoardHome;
